import type { StepOptions } from 'shepherd.js'

/**
 * Shift a step's Shepherd popover by an absolute pixel offset from where
 * FloatingUI computed its position. The deltas are in screen-space (x, y),
 * NOT main/cross axis — they work regardless of `attachTo.on` placement.
 *
 * Use this when:
 *   - A `bottom`/`top`-placed popover visually touches the anchor edge and
 *     needs a small gap (try `dy = 12`).
 *   - A `right`/`left`-placed popover overlaps a portal-rendered child
 *     (e.g. a Radix Popover that opens below the anchor); push the popover
 *     along the main axis to clear it.
 *   - The popover would overflow the viewport on a short anchor near the
 *     viewport edge and FloatingUI's default shift looks awkward.
 *
 * See [docs/guides/tours.md §4.3] for the full rationale + worked examples.
 *
 * Example:
 *   floatingUIOptions: popoverOffset(0, 12),   // push down 12px
 *   floatingUIOptions: popoverOffset(140, 20), // push right + down
 *   floatingUIOptions: popoverOffset(-20, 12), // push left + down
 */
export function popoverOffset(dx: number, dy: number): NonNullable<StepOptions['floatingUIOptions']> {
  return {
    middleware: [{
      name: 'shepherd-popover-offset',
      fn: ({ x, y }: { x: number, y: number }) => ({ x: x + dx, y: y + dy }),
    }],
  }
}
